import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    RadialBarChart,
    RadialBar
} from 'recharts';

const StatsOverview = () => {
    const { language } = useLanguage();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedLevel, setSelectedLevel] = useState('ppl');

    useEffect(() => {
        const loadStats = async () => {
            try {
                const data = await api.getStats(selectedLevel);
                setStats(data);
            } catch (error) {
                console.error("Failed to load stats", error);
            } finally {
                setLoading(false);
            }
        };
        loadStats();
    }, [selectedLevel]);

    if (loading) {
        return <div className="p-4 text-center text-gray-500">{language === 'fr' ? 'Chargement...' : 'Loading...'}</div>;
    }

    if (!stats || !stats.all || !stats.all.history || stats.all.history.length === 0) {
        return <div className="p-4 text-center text-gray-500">{language === 'fr' ? 'Aucune donnée disponible.' : 'No data available.'}</div>;
    }

    const currentStats = stats.all;

    return (
        <div className="space-y-6">
            {/* Header / Filter */}
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow">
                <h2 className="text-xl font-bold text-gray-800">
                    {language === 'fr' ? 'Mes Performances' : 'My Performance'}
                </h2>
                <select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    className="border border-gray-300 rounded-md shadow-sm px-3 py-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="ppl">PPL</option>
                    <option value="pstar">PSTAR</option>
                    <option value="ifr">IFR</option>
                </select>
            </div>

            {/* Main History Chart */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-700 mb-4">
                    {language === 'fr' ? 'Progression Globale' : 'Overall Progression'}
                </h3>
                <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={currentStats.history}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="score"
                                stroke="#3b82f6"
                                activeDot={{ r: 8 }}
                                name={language === 'fr' ? 'Score Moyen' : 'Average Score'}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Module Breakdown Circles */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {(currentStats.moduleScores || []).map((module, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow flex flex-col items-center">
                        <h4 className="text-md font-semibold text-gray-600 mb-2">{module.name}</h4>
                        <div className="w-full h-40">
                            <ResponsiveContainer width="100%" height="100%">
                                <RadialBarChart
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="60%"
                                    outerRadius="80%"
                                    barSize={10}
                                    data={[{ name: module.name, value: module.score, fill: module.score >= 75 ? '#22c55e' : '#f59e0b' }]}
                                    startAngle={90}
                                    endAngle={-270}
                                >
                                    <RadialBar
                                        minAngle={15}
                                        background
                                        clockWise
                                        dataKey="value"
                                    />
                                    <text
                                        x="50%"
                                        y="50%"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        className="text-xl font-bold fill-gray-700"
                                    >
                                        {module.score}%
                                    </text>
                                </RadialBarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StatsOverview;
